import React, { useContext } from "react";
import { createPortal } from "react-dom";

import styled from "styled-components";
import { TbCloudUpload, TbInfoCircle, TbVideo } from "react-icons/tb";
import { IoCheckmark, IoLockClosed } from "react-icons/io5";
import { Box, Flex, Image, Text } from "rebass/styled-components";
import { useState, useRef, useEffect, useCallback } from "react";
import { useRecoilState } from "recoil";
import { AiFillCloseCircle } from "react-icons/ai";
import axios from "axios";
import { useLazyQuery } from "@apollo/client";

import theme from "theme/themeThree";
import { BaseSection } from "../styledComponents";
import { IoCheckmarkSharp } from "react-icons/io5";
import { LoadingSpinner } from "MVVM/Components/Lottie";
import {
  QuickEditPreferredFinalTouches,
  QuickEditPreferredThumbnail,
} from "MVVM/State/quickEdit";
import { usePortal } from "MVVM/Hooks/usePortal";
import { SignatureSelectorModal } from "MVVM/Components/Signatures";
import { analytics } from "MVVM/Analytics";
import { IQuickEditMoment } from "../@types";
import { useUser } from "MVVM/Hooks/useUser";
import { Button } from "../../Button";
import { Tooltip } from "MVVM/Components/Tooltip";
import { GET_MY_SIGNATURE_QUICK_EDIT } from "MVVM/GraphQL/quickEdit";
import { TClipCard } from "MVVM/State/loadoutSelector";
import { TooltipPortalContext, TQuickEditType } from "../index";
import { useShouldShowSubscriptionOverhaul } from "MVVM/Hooks/useSubscriptionOverhaul";
import { UnifiedUpgradeModal } from "MVVM/Components/Modals/UnifiedUpgradeModal";
import {
  getTierAccentColor,
  getTierButtonGradient,
} from "MVVM/Utilities/SubscriptionTiers";

export const FinalTouches = ({
  selectedFinalTouches,
  setSelectedFinalTouches,
  setThumbnailKey,
  mediaUploadUrl,
  isOwnMoment,
  moment,
  hideSignature,
  disablePreferredTouches,
  closeModal,
  section,
  openThumbnailUpload,
  type,
}: {
  selectedFinalTouches: string[];
  setSelectedFinalTouches: (finalTouches: string[]) => void;
  setThumbnailKey: (thumbnailKey: string) => void;
  mediaUploadUrl?: {
    fields: {
      name: string;
      value: string;
    }[];
    key: string;
    url: string;
  };
  isOwnMoment: boolean;
  moment: IQuickEditMoment;
  hideSignature?: boolean;
  disablePreferredTouches?: boolean;
  closeModal?: () => void;
  section?: "signature" | "effects" | "thumbnail" | "overlay";
  openThumbnailUpload?: boolean;
  type?: TQuickEditType;
}) => {
  const { allstarUser } = useUser();
  const showSubscriptionOverhaul = useShouldShowSubscriptionOverhaul();
  const [paywallFeatureName, setPaywallFeatureName] = useState<string>("");
  const [paywallUpgradeAction, setPaywallUpgradeAction] = useState<string>("");
  const [selectedFileUrl, setSelectedFileUrl] = useState<string | null>(null);
  const [preferredThumbnail, setPreferredThumbnail] = useRecoilState(
    QuickEditPreferredThumbnail,
  );
  const [preferredFinalTouches, setPreferredFinalTouches] = useRecoilState(
    QuickEditPreferredFinalTouches,
  );
  const [userHasSignature, setUserHasSignature] = useState<boolean | null>(
    null,
  );
  const [userHasSignatureMusic, setUserHasSignatureMusic] = useState<
    boolean | null
  >(null);
  const [getMySignature] = useLazyQuery(GET_MY_SIGNATURE_QUICK_EDIT, {
    fetchPolicy: "network-only",
  });
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState<boolean>(false);

  useEffect(() => {
    return () => {
      if (selectedFileUrl) URL.revokeObjectURL(selectedFileUrl);
    };
  }, [selectedFileUrl]);

  const isSecondary = ["secondary", "secondary_fail"].includes(moment.type);
  const signatureDisabled = !isOwnMoment;
  const rapidFireDisabled = isSecondary;
  const contextCamsDisabled = isSecondary;

  const isPremiumDisabled = showSubscriptionOverhaul
    ? allstarUser?.user?.userGroup?.type === "FREE"
    : allstarUser?.user?.userGroup?.type === "FREE" ||
      allstarUser?.user?.userGroup?.type === "ESSENTIALS";
  const allFeaturesLocked = showSubscriptionOverhaul && isPremiumDisabled;
  const hideWatermarkDisabled = isPremiumDisabled;
  const hideKillFeedDisabled = isPremiumDisabled;
  const mountedCamerasDisabled = isPremiumDisabled;
  const finalKillCameraDisabled = isPremiumDisabled;
  const disableJumpCutsDisabled = isPremiumDisabled;

  const isCounterStrike = moment.game === "CS2";

  const fetchUserSignature = useCallback(
    (signal?: { cancelled: boolean }) => {
      if (allstarUser?.user?._id) {
        getMySignature({
          variables: {
            user: allstarUser.user._id,
            signature: true,
          },
        }).then(({ data }) => {
          if (signal?.cancelled) return;

          const hasSignature =
            (data?.getLoadouts?.metadataCustomStyleSelection?.length ?? 0) > 0;

          const hasMusicSelected =
            hasSignature &&
            data.getLoadouts?.clipCards?.some(
              (clipCard: TClipCard) => clipCard.card.effect === "FACET_LOOKUP",
            );

          setUserHasSignature(hasSignature);
          setUserHasSignatureMusic(hasMusicSelected);
        });
      }
    },
    [allstarUser?.user?._id, getMySignature],
  );

  useEffect(() => {
    if (allstarUser?.loggedIn && userHasSignature === null) {
      const signal = { cancelled: false };
      fetchUserSignature(signal);
      return () => {
        signal.cancelled = true;
      };
    }
  }, [allstarUser?.loggedIn, userHasSignature, fetchUserSignature]);

  const {
    portalOpen: signatureSelectorOpen,
    setPortalOpen: setSignatureSelectorOpen,
    Portal: SignatureSelectorPortal,
  } = usePortal(
    <SignatureSelectorModal
      onSave={() => {
        setSignatureSelectorOpen(false);
        fetchUserSignature();
      }}
      onCloseClick={() => {
        setSignatureSelectorOpen(false);
      }}
    />,
  );

  const {
    portalOpen: subscriptionPaywallOpen,
    setPortalOpen: setSubscriptionPaywallOpen,
    Portal: SubscriptionPaywallPortal,
  } = usePortal(
    <UnifiedUpgradeModal
      featureName={paywallFeatureName}
      upgradeAction={paywallUpgradeAction}
      requiredTierKey="ESSENTIALS"
      analyticsSource="Quick Edit"
      onClose={() => setSubscriptionPaywallOpen(false)}
    />,
  );

  useEffect(() => {
    if (preferredThumbnail && !disablePreferredTouches) {
      setSelectedFileUrl(preferredThumbnail);
      setThumbnailKey(preferredThumbnail);
    }
  }, [preferredThumbnail, setThumbnailKey, disablePreferredTouches]);

  useEffect(() => {
    if (disablePreferredTouches) return;

    const disabledTouches: Record<string, boolean> = {
      signature: signatureDisabled || userHasSignature === false,
      music: signatureDisabled,
      rapidFire: rapidFireDisabled || type !== "MONTAGE",
      contextCams: contextCamsDisabled,
      hideWatermark: hideWatermarkDisabled,
      hideKillFeed: hideKillFeedDisabled,
      mountedCameras: mountedCamerasDisabled,
      finalKillCamera: finalKillCameraDisabled,
      disableJumpCuts: disableJumpCutsDisabled,
    };

    setSelectedFinalTouches(
      preferredFinalTouches.filter((t) => !disabledTouches[t]),
    );
  }, [
    preferredFinalTouches,
    signatureDisabled,
    rapidFireDisabled,
    contextCamsDisabled,
    hideWatermarkDisabled,
    hideKillFeedDisabled,
    mountedCamerasDisabled,
    finalKillCameraDisabled,
    disableJumpCutsDisabled,
    setSelectedFinalTouches,
    disablePreferredTouches,
    userHasSignature,
    type,
  ]);

  const { REACT_APP_S3_CLIP_BUCKET } = process.env;

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (file) {
      setUploading(true);
      if (selectedFileUrl) URL.revokeObjectURL(selectedFileUrl);
      const fileUrl = URL.createObjectURL(file);
      const formData = new FormData();

      for (const field of mediaUploadUrl?.fields ?? []) {
        formData.append(field.name, field.value ?? "");
      }
      formData.append("file", file);

      try {
        await axios.post(mediaUploadUrl?.url ?? "", formData);

        setSelectedFileUrl(fileUrl);
        if (mediaUploadUrl?.key) {
          setThumbnailKey(`${mediaUploadUrl.url}/${mediaUploadUrl.key}`);
        }
        setUploading(false);
      } catch (error) {
        setUploading(false);
      }
    }
  };

  const handleThumbnailClick = () => {
    fileInputRef.current?.click();
  };

  const handlePremiumFeatureClick = (
    featureName: string,
    upgradeAction: string,
  ) => {
    analytics.track("Upgrade CTA Clicked", {
      location: `Quick Edit - ${featureName}`,
    });

    setPaywallFeatureName(featureName);
    setPaywallUpgradeAction(upgradeAction);
    setSubscriptionPaywallOpen(true);
  };

  const handleToggle = (touch: string) => {
    if (selectedFinalTouches.includes(touch)) {
      setPreferredFinalTouches(
        preferredFinalTouches.filter((t) => t !== touch),
      );
      setSelectedFinalTouches(selectedFinalTouches.filter((t) => t !== touch));
    } else {
      if (touch === "signature" && userHasSignature === false) {
        analytics.track("Signature - Open Modal", {
          where: "Quick Edit",
          type: "First Time Setup",
        });
        setSignatureSelectorOpen(true);
      }
      setPreferredFinalTouches([...preferredFinalTouches, touch]);
      setSelectedFinalTouches([...selectedFinalTouches, touch]);
    }
  };

  const {
    portalOpen: thumbnailSelectorOpen,
    setPortalOpen: setThumbnailSelectorOpen,
    Portal: ThumbnailSelectorPortal,
  } = usePortal(
    <ThumbnailSection>
      <Text
        variant="text.labelSmall"
        style={{
          width: "100%",
          justifyContent: "start",
          color: theme.colors.chalk,
        }}
      >
        Clip Thumbnail:
      </Text>

      <input
        type="file"
        accept="image/*"
        onChange={handleFileChange}
        ref={fileInputRef}
        style={{ display: "none" }}
      />

      <Flex style={{ position: "relative", width: "100%" }}>
        <ThumbnailArea
          onClick={handleThumbnailClick}
          showBorder={!selectedFileUrl}
        >
          {selectedFileUrl ? (
            <PreviewThumbnail src={selectedFileUrl} />
          ) : (
            <>
              {uploading ? (
                <LoadingSpinner height={30} width={30} />
              ) : (
                <Flex
                  flexDirection={"column"}
                  alignItems={"center"}
                  style={{
                    gap: 8,
                  }}
                >
                  <UploadIcon size={30} />
                  <Text variant="text.labelMedium" color={theme.colors.chalk}>
                    Customize Your Thumbnail
                  </Text>
                  <Text
                    variant="text.labelXXSmall"
                    color={theme.colors.chalk}
                    textAlign={"center"}
                    maxWidth={300}
                  >
                    Upload an optional PNG, JPEG or GIF as a custom thumbnail,
                    or we'll use your Signature or current loadout.
                  </Text>
                </Flex>
              )}
            </>
          )}
        </ThumbnailArea>
      </Flex>
      <Flex
        style={{
          position: "relative",
          width: "100%",
          gap: 12,
          justifyContent: "center",
          alignItems: "center",
          paddingTop: 12,
        }}
      >
        <Button
          classification="roundedFilledAlt"
          size="small"
          onClick={() => {
            if (selectedFileUrl) URL.revokeObjectURL(selectedFileUrl);
            setPreferredThumbnail("");
            setSelectedFileUrl(null);
            setThumbnailKey("");
            setThumbnailSelectorOpen(false);
          }}
          flex={true}
        >
          Cancel
        </Button>
        <Button
          classification="roundedFilled"
          size="small"
          flex={true}
          disabled={!selectedFileUrl}
          onClick={() => setThumbnailSelectorOpen(false)}
        >
          Save
        </Button>
      </Flex>
    </ThumbnailSection>,
  );

  useEffect(() => {
    if (openThumbnailUpload) {
      setThumbnailSelectorOpen(true);
    }
  }, [openThumbnailUpload, setThumbnailSelectorOpen]);

  const showThumbnail = !section || section === "thumbnail";
  const showSignature = !section || section === "signature";
  const showEffects =
    !section || section === "effects" || section === "overlay";
  const showOverlayOnly = section === "overlay";

  const essentialsAccent = getTierAccentColor("ESSENTIALS");
  const essentialsGradient = getTierButtonGradient("ESSENTIALS");

  if (allFeaturesLocked && showSignature && section === "signature") {
    return (
      <>
        {subscriptionPaywallOpen && SubscriptionPaywallPortal}
        <UpsellPanel>
          <UpsellHeader $gradient="linear-gradient(135deg, rgba(78, 189, 148, 0.15) 0%, transparent 60%)">
            <IoLockClosed size={14} color={theme.colors.chalk} />
            <Text variant="text.labelSmall" color={theme.colors.chalk}>
              Unlock with{" "}
              <span style={{ color: essentialsAccent, fontWeight: 700 }}>
                Basic
              </span>
            </Text>
          </UpsellHeader>
          <UpsellFeatureList>
            <UpsellFeatureItem $color={essentialsAccent}>
              <IoCheckmark size={16} />
              <Text variant="text.labelXSmall" color={theme.colors.ice}>
                Custom thumbnails on your clips
              </Text>
            </UpsellFeatureItem>
            <UpsellFeatureItem $color={essentialsAccent}>
              <IoCheckmark size={16} />
              <Text variant="text.labelXSmall" color={theme.colors.ice}>
                Add your Signature intro/outro
              </Text>
            </UpsellFeatureItem>
            <UpsellFeatureItem $color={essentialsAccent}>
              <IoCheckmark size={16} />
              <Text variant="text.labelXSmall" color={theme.colors.ice}>
                Signature music on your clips
              </Text>
            </UpsellFeatureItem>
          </UpsellFeatureList>
          <TierUpsellButton
            $gradient={essentialsGradient}
            onClick={() =>
              handlePremiumFeatureClick(
                "Signature & Thumbnail",
                "Use Signatures & Thumbnails",
              )
            }
          >
            Upgrade to Basic
          </TierUpsellButton>
        </UpsellPanel>
      </>
    );
  }

  if (allFeaturesLocked && showEffects && section === "effects") {
    return (
      <>
        {subscriptionPaywallOpen && SubscriptionPaywallPortal}
        <UpsellPanel>
          <UpsellHeader $gradient="linear-gradient(135deg, rgba(78, 189, 148, 0.15) 0%, transparent 60%)">
            <IoLockClosed size={14} color={theme.colors.chalk} />
            <Text variant="text.labelSmall" color={theme.colors.chalk}>
              Unlock with{" "}
              <span style={{ color: essentialsAccent, fontWeight: 700 }}>
                Basic
              </span>
            </Text>
          </UpsellHeader>
          <UpsellFeatureList>
            <UpsellFeatureItem $color={essentialsAccent}>
              <IoCheckmark size={16} />
              <Text variant="text.labelXSmall" color={theme.colors.ice}>
                Camera effects & context cams
              </Text>
            </UpsellFeatureItem>
            <UpsellFeatureItem $color={essentialsAccent}>
              <IoCheckmark size={16} />
              <Text variant="text.labelXSmall" color={theme.colors.ice}>
                Rapid Fire & jump cut controls
              </Text>
            </UpsellFeatureItem>
            <UpsellFeatureItem $color={essentialsAccent}>
              <IoCheckmark size={16} />
              <Text variant="text.labelXSmall" color={theme.colors.ice}>
                Hide watermark & kill feed
              </Text>
            </UpsellFeatureItem>
          </UpsellFeatureList>
          <TierUpsellButton
            $gradient={essentialsGradient}
            onClick={() =>
              handlePremiumFeatureClick(
                "Advanced Effects",
                "Use Advanced Effects",
              )
            }
          >
            Upgrade to Basic
          </TierUpsellButton>
        </UpsellPanel>
      </>
    );
  }

  return (
    <>
      {signatureSelectorOpen && SignatureSelectorPortal}
      {thumbnailSelectorOpen && ThumbnailSelectorPortal}
      {subscriptionPaywallOpen && SubscriptionPaywallPortal}
      <Section>
        {!section && (
          <Text
            variant="text.labelSmall"
            style={{
              width: "100%",
              justifyContent: "start",
              color: theme.colors.chalk,
            }}
          >
            Choose your finishing touches:
          </Text>
        )}
        <ToggleContainer>
          {showSignature && (
            <>
              {!hideSignature && (
                <>
                  <ToggleColumn>
                    <ToggleRow
                      $active={selectedFinalTouches.includes("signature")}
                    >
                      <FeatureTooltip
                        title="Add My Signature"
                        description="Signatures allow you to add a personalized intro/nameplate/outro to your clips, to show off your stats to viewers."
                      >
                        <Flex alignItems="center" style={{ gap: 4 }}>
                          <Text variant="text.labelSmall">
                            Add My Signature
                          </Text>
                          <InfoTooltipIcon />
                        </Flex>
                      </FeatureTooltip>
                      <Checkbox
                        $checked={selectedFinalTouches.includes("signature")}
                        $disabled={signatureDisabled}
                        onClick={() =>
                          !signatureDisabled && handleToggle("signature")
                        }
                      >
                        {selectedFinalTouches.includes("signature") && (
                          <IoCheckmarkSharp size={12} />
                        )}
                      </Checkbox>
                    </ToggleRow>
                    {signatureDisabled && (
                      <Text
                        variant="text.labelXXSmall"
                        color={theme.colors.error}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 4,
                        }}
                      >
                        <InfoIcon style={{ marginTop: 2 }} />
                        Signatures are not yet supported for Moments that are
                        not your own.
                      </Text>
                    )}
                    <ManageSignatureLink
                      variant="text.labelSmall"
                      color="chalkAlpha60"
                      onClick={() => {
                        analytics.track("Signature - Open Modal", {
                          where: "Quick Edit",
                          type: "Manage",
                        });
                        setSignatureSelectorOpen(true);
                      }}
                    >
                      <Image
                        src={`${process.env.REACT_APP_S3_CLIP_BUCKET}/static/loadouts/sig.svg`}
                        height={12}
                        width={12}
                        mr="6px"
                      />
                      Manage My Signature
                    </ManageSignatureLink>
                  </ToggleColumn>

                  <ToggleColumn>
                    <ToggleRow $active={selectedFinalTouches.includes("music")}>
                      <FeatureTooltip
                        title="Add Signature Music"
                        description="If you have music genres selected in your Signature, we'll choose a matching song to add to your clips."
                      >
                        <Flex alignItems="center" style={{ gap: 4 }}>
                          <Text variant="text.labelSmall">
                            Add Signature Music
                          </Text>
                          <InfoTooltipIcon />
                        </Flex>
                      </FeatureTooltip>

                      <Checkbox
                        $checked={selectedFinalTouches.includes("music")}
                        $disabled={!selectedFinalTouches.includes("signature")}
                        onClick={() =>
                          selectedFinalTouches.includes("signature") &&
                          handleToggle("music")
                        }
                      >
                        {selectedFinalTouches.includes("music") && (
                          <IoCheckmarkSharp size={12} />
                        )}
                      </Checkbox>
                    </ToggleRow>

                    {signatureDisabled && (
                      <Text
                        variant="text.labelXXSmall"
                        color={theme.colors.error}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 4,
                        }}
                      >
                        <InfoIcon style={{ marginTop: 2 }} />
                        Signatures are not yet supported for Moments that are
                        not your own.
                      </Text>
                    )}

                    {!signatureDisabled &&
                      !selectedFinalTouches.includes("signature") &&
                      selectedFinalTouches.includes("music") && (
                        <WarningText
                          variant="text.labelXXSmall"
                          color={theme.colors.alert}
                        >
                          Signature must be enabled to use music. Enable "Add My
                          Signature" above.
                        </WarningText>
                      )}

                    {!signatureDisabled &&
                      selectedFinalTouches.includes("signature") &&
                      userHasSignatureMusic === false &&
                      selectedFinalTouches.includes("music") && (
                        <WarningText
                          variant="text.labelXXSmall"
                          color={theme.colors.alert}
                        >
                          You do not have music genres selected in your
                          Signature. Click "Manage My Signature" above to add
                          genres.
                        </WarningText>
                      )}
                  </ToggleColumn>
                </>
              )}
            </>
          )}

          {showEffects && (
            <>
              {!showOverlayOnly && (
                <>
                  <HintText>
                    Hint: Hover an effect to see more info or a preview
                  </HintText>
                  {isCounterStrike && (
                    <>
                      <CategoryHeader>Camera FX</CategoryHeader>
                      <ToggleColumn>
                        <ToggleRow
                          $active={selectedFinalTouches.includes("contextCams")}
                        >
                          <VideoFeatureTooltip
                            title="Add Context Cams"
                            description="Before a gunfight that ends with 1 or more kills, we'll show a raised camera view of the area, giving your viewers extra context about what happened."
                            videoSrc="cards/thumbnails/specialfx/mp4/envcams_thumbnail.mp4"
                          >
                            <Flex alignItems="center" style={{ gap: 4 }}>
                              <Text variant="text.labelSmall">
                                Context Cams
                              </Text>
                              <InfoTooltipIcon />
                            </Flex>
                          </VideoFeatureTooltip>
                          <Checkbox
                            $checked={selectedFinalTouches.includes(
                              "contextCams",
                            )}
                            $disabled={contextCamsDisabled}
                            onClick={() =>
                              !contextCamsDisabled &&
                              handleToggle("contextCams")
                            }
                          >
                            {selectedFinalTouches.includes("contextCams") && (
                              <IoCheckmarkSharp size={12} />
                            )}
                          </Checkbox>
                        </ToggleRow>
                        {contextCamsDisabled && (
                          <Text
                            variant="text.labelXXSmall"
                            color={theme.colors.error}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 4,
                            }}
                          >
                            <InfoIcon style={{ marginTop: 2 }} />
                            Not supported for Fail Moments.
                          </Text>
                        )}
                      </ToggleColumn>
                      <FeatureToggleColumn
                        disabled={mountedCamerasDisabled}
                        onClick={() =>
                          mountedCamerasDisabled
                            ? handlePremiumFeatureClick(
                                "Camera Effects",
                                "Use Camera Effects",
                              )
                            : undefined
                        }
                      >
                        <ToggleRow
                          $active={selectedFinalTouches.includes(
                            "mountedCameras",
                          )}
                        >
                          <VideoFeatureTooltip
                            title="Add Camera Effects"
                            description="Enable player follow cams for more cinematic shots."
                            videoSrc="cards/thumbnails/specialfx/mp4/guncam_thumbnail.mp4"
                          >
                            <Flex alignItems="center" style={{ gap: 4 }}>
                              <Text variant="text.labelSmall">
                                Camera Effects
                              </Text>
                              <InfoTooltipIcon />
                            </Flex>
                          </VideoFeatureTooltip>
                          <Checkbox
                            $checked={selectedFinalTouches.includes(
                              "mountedCameras",
                            )}
                            $disabled={mountedCamerasDisabled}
                            onClick={() =>
                              !mountedCamerasDisabled &&
                              handleToggle("mountedCameras")
                            }
                          >
                            {selectedFinalTouches.includes(
                              "mountedCameras",
                            ) && <IoCheckmarkSharp size={12} />}
                          </Checkbox>
                        </ToggleRow>
                      </FeatureToggleColumn>
                      <FeatureToggleColumn
                        disabled={finalKillCameraDisabled}
                        onClick={() =>
                          finalKillCameraDisabled
                            ? handlePremiumFeatureClick(
                                "Final Kill Cam",
                                "Use Final Kill Cam",
                              )
                            : undefined
                        }
                      >
                        <ToggleRow
                          $active={selectedFinalTouches.includes(
                            "finalKillCamera",
                          )}
                        >
                          <FeatureTooltip
                            title="Final Kill Cam"
                            description="On the last kill, we'll switch views to the enemy player. BONUS: When enabled, this will hide your deaths at the end of the clip!"
                          >
                            <Flex alignItems="center" style={{ gap: 4 }}>
                              <Text variant="text.labelSmall">
                                Final Kill Cam
                              </Text>
                              <InfoTooltipIcon />
                            </Flex>
                          </FeatureTooltip>
                          <Checkbox
                            $checked={selectedFinalTouches.includes(
                              "finalKillCamera",
                            )}
                            $disabled={finalKillCameraDisabled}
                            onClick={() =>
                              !finalKillCameraDisabled &&
                              handleToggle("finalKillCamera")
                            }
                          >
                            {selectedFinalTouches.includes(
                              "finalKillCamera",
                            ) && <IoCheckmarkSharp size={12} />}
                          </Checkbox>
                        </ToggleRow>
                      </FeatureToggleColumn>
                    </>
                  )}

                  {isCounterStrike && (
                    <>
                      <CategoryHeader>Editing</CategoryHeader>
                      {type === "MONTAGE" && (
                        <ToggleColumn>
                          <ToggleRow
                            $active={selectedFinalTouches.includes("rapidFire")}
                          >
                            <FeatureTooltip
                              title="Rapid Fire"
                              description="Love Jump Cuts, but want something even faster? Enable Rapid Fire to see just the kills. Does not work with Disable Jump Cuts enabled."
                            >
                              <Flex alignItems="center" style={{ gap: 4 }}>
                                <Text variant="text.labelSmall">
                                  Rapid Fire
                                </Text>
                                <InfoTooltipIcon />
                              </Flex>
                            </FeatureTooltip>
                            <Checkbox
                              $checked={selectedFinalTouches.includes(
                                "rapidFire",
                              )}
                              $disabled={
                                rapidFireDisabled ||
                                selectedFinalTouches.includes("disableJumpCuts")
                              }
                              onClick={() =>
                                !(
                                  rapidFireDisabled ||
                                  selectedFinalTouches.includes(
                                    "disableJumpCuts",
                                  )
                                ) && handleToggle("rapidFire")
                              }
                            >
                              {selectedFinalTouches.includes("rapidFire") && (
                                <IoCheckmarkSharp size={12} />
                              )}
                            </Checkbox>
                          </ToggleRow>
                          {(rapidFireDisabled ||
                            selectedFinalTouches.includes(
                              "disableJumpCuts",
                            )) && (
                            <Text
                              variant="text.labelXXSmall"
                              color={theme.colors.error}
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 4,
                              }}
                            >
                              <InfoIcon
                                style={{ marginTop: 2, flexShrink: 0 }}
                              />
                              <span>
                                {rapidFireDisabled
                                  ? "Not supported for Fail Moments."
                                  : "Not supported when Disable Jump Cuts is enabled."}
                              </span>
                            </Text>
                          )}
                        </ToggleColumn>
                      )}
                      <FeatureToggleColumn
                        disabled={
                          disableJumpCutsDisabled &&
                          !selectedFinalTouches.includes("rapidFire")
                        }
                        onClick={() =>
                          disableJumpCutsDisabled
                            ? handlePremiumFeatureClick(
                                "Disable Jump Cuts",
                                "Disable Jump Cuts",
                              )
                            : undefined
                        }
                      >
                        <ToggleRow
                          $active={selectedFinalTouches.includes(
                            "disableJumpCuts",
                          )}
                        >
                          <FeatureTooltip
                            title="Disable Jump Cuts"
                            description="Show the full sequence from first-to-last kill instead of cuts. Does not work with Rapid Fire enabled."
                          >
                            <Flex alignItems="center" style={{ gap: 4 }}>
                              <Text variant="text.labelSmall">
                                Disable Jump Cuts
                              </Text>
                              <InfoTooltipIcon />
                            </Flex>
                          </FeatureTooltip>
                          <Checkbox
                            $checked={selectedFinalTouches.includes(
                              "disableJumpCuts",
                            )}
                            $disabled={
                              disableJumpCutsDisabled ||
                              selectedFinalTouches.includes("rapidFire")
                            }
                            onClick={() =>
                              !(
                                disableJumpCutsDisabled ||
                                selectedFinalTouches.includes("rapidFire")
                              ) && handleToggle("disableJumpCuts")
                            }
                          >
                            {selectedFinalTouches.includes(
                              "disableJumpCuts",
                            ) && <IoCheckmarkSharp size={12} />}
                          </Checkbox>
                        </ToggleRow>
                      </FeatureToggleColumn>
                    </>
                  )}
                </>
              )}

              {!showOverlayOnly && <CategoryHeader>Overlay</CategoryHeader>}
              <FeatureToggleColumn
                disabled={hideWatermarkDisabled}
                onClick={() =>
                  hideWatermarkDisabled
                    ? handlePremiumFeatureClick(
                        "Hide Watermark",
                        "Hide Watermark",
                      )
                    : undefined
                }
              >
                <ToggleRow
                  $active={selectedFinalTouches.includes("hideWatermark")}
                >
                  <VideoFeatureTooltip
                    title="Hide Watermark"
                    description="Remove the Allstar watermark from your clips for a cleaner look."
                    videoSrc="cards/watermarks/no_watermark_mp4/v1/ui/no_watermark_mp4.mp4"
                  >
                    <Flex alignItems="center" style={{ gap: 4 }}>
                      <Text variant="text.labelSmall">Hide Watermark</Text>
                      <InfoTooltipIcon />
                    </Flex>
                  </VideoFeatureTooltip>
                  <Checkbox
                    $checked={selectedFinalTouches.includes("hideWatermark")}
                    $disabled={hideWatermarkDisabled}
                    onClick={() =>
                      !hideWatermarkDisabled && handleToggle("hideWatermark")
                    }
                  >
                    {selectedFinalTouches.includes("hideWatermark") && (
                      <IoCheckmarkSharp size={12} />
                    )}
                  </Checkbox>
                </ToggleRow>
              </FeatureToggleColumn>

              {isCounterStrike && (
                <FeatureToggleColumn
                  disabled={hideKillFeedDisabled}
                  onClick={() =>
                    hideKillFeedDisabled
                      ? handlePremiumFeatureClick(
                          "Hide Kill Feed",
                          "Hide Kill Feed",
                        )
                      : undefined
                  }
                >
                  <ToggleRow
                    $active={selectedFinalTouches.includes("hideKillFeed")}
                  >
                    <VideoFeatureTooltip
                      title="Hide Kill Feed"
                      description="Remove the kill feed from your clips for a cleaner viewing experience."
                      videoSrc="cards/overlay/hidekillfeed_mp4/v1/ui/hidekillfeed_mp4.mp4"
                    >
                      <Flex alignItems="center" style={{ gap: 4 }}>
                        <Text variant="text.labelSmall">Hide Kill Feed</Text>
                        <InfoTooltipIcon />
                      </Flex>
                    </VideoFeatureTooltip>
                    <Checkbox
                      $checked={selectedFinalTouches.includes("hideKillFeed")}
                      $disabled={hideKillFeedDisabled}
                      onClick={() =>
                        !hideKillFeedDisabled && handleToggle("hideKillFeed")
                      }
                    >
                      {selectedFinalTouches.includes("hideKillFeed") && (
                        <IoCheckmarkSharp size={12} />
                      )}
                    </Checkbox>
                  </ToggleRow>
                </FeatureToggleColumn>
              )}

              {isPremiumDisabled && (
                <TierUpsellDivider $tierColor={essentialsAccent}>
                  <TierUpsellButton
                    $gradient={essentialsGradient}
                    onClick={() =>
                      handlePremiumFeatureClick(
                        "Premium Features",
                        "Use Premium Features",
                      )
                    }
                  >
                    Upgrade to Basic
                  </TierUpsellButton>
                </TierUpsellDivider>
              )}
            </>
          )}
        </ToggleContainer>
      </Section>
    </>
  );
};

const FeatureTooltip = ({
  title,
  description,
  children,
}: {
  title: string;
  description: string;
  children: React.ReactNode;
}) => {
  const portalTarget = useContext(TooltipPortalContext);
  const [isHovered, setIsHovered] = useState(false);
  const triggerRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState({ top: 0, left: 0 });

  useEffect(() => {
    if (isHovered && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const containerRect = portalTarget?.getBoundingClientRect();
      if (containerRect) {
        setPosition({
          top: rect.bottom - containerRect.top + 8,
          left: rect.left - containerRect.left + rect.width / 2,
        });
      }
    }
  }, [isHovered, portalTarget]);

  const tooltipContent = (
    <PortalTooltipContent
      style={{ top: position.top, left: position.left }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <TooltipContent>
        <TooltipTitle variant="text.labelSmall" color="ice" fontWeight={600}>
          {title}
        </TooltipTitle>
        <TooltipDescription variant="text.labelXSmall" color="chalkAlpha80">
          {description}
        </TooltipDescription>
      </TooltipContent>
    </PortalTooltipContent>
  );

  return (
    <TooltipTrigger
      ref={triggerRef}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {children}
      {isHovered && portalTarget && createPortal(tooltipContent, portalTarget)}
    </TooltipTrigger>
  );
};

const VideoFeatureTooltip = ({
  title,
  description,
  videoSrc,
  children,
}: {
  title: string;
  description: string;
  videoSrc: string;
  children: React.ReactNode;
}) => {
  const portalTarget = useContext(TooltipPortalContext);
  const [isHovered, setIsHovered] = useState(false);
  const triggerRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const videoUrl = `${process.env.REACT_APP_S3_CLIP_BUCKET}/static/${videoSrc}`;

  useEffect(() => {
    if (isHovered && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const containerRect = portalTarget?.getBoundingClientRect();
      if (containerRect) {
        setPosition({
          top: rect.bottom - containerRect.top + 8,
          left: rect.left - containerRect.left + rect.width / 2,
        });
      }
    }
  }, [isHovered, portalTarget]);

  const tooltipContent = (
    <PortalTooltipContent
      style={{ top: position.top, left: position.left }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <VideoTooltipContent>
        <TooltipTitle variant="text.labelSmall" color="ice" fontWeight={600}>
          {title}
        </TooltipTitle>
        <TooltipDescription variant="text.labelXSmall" color="chalkAlpha80">
          {description}
        </TooltipDescription>
        <VideoPreviewContainer>
          <VideoPreview src={videoUrl} autoPlay loop muted playsInline />
        </VideoPreviewContainer>
      </VideoTooltipContent>
    </PortalTooltipContent>
  );

  return (
    <TooltipTrigger
      ref={triggerRef}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {children}
      {isHovered && portalTarget && createPortal(tooltipContent, portalTarget)}
    </TooltipTrigger>
  );
};

const TooltipTrigger = styled.div`
  display: inline-block;
  cursor: pointer;
`;

const PortalTooltipContent = styled.div`
  position: absolute;
  transform: translateX(-50%);
  z-index: 1000;
  pointer-events: auto;
  animation: tooltipFadeIn 200ms ease-out;

  @keyframes tooltipFadeIn {
    from {
      opacity: 0;
      transform: translateX(-50%) translateY(-4px);
    }
    to {
      opacity: 1;
      transform: translateX(-50%) translateY(0);
    }
  }
`;

const TooltipContent = styled(Flex)`
  align-items: start;
  background-color: ${theme.colors.grey850};
  border-radius: 8px;
  border: 1px solid ${theme.colors.chalkAlpha20};
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);
  flex-direction: column;
  gap: 6px;
  padding: 14px;
  width: max-content;
`;

const VideoTooltipContent = styled(Flex)`
  align-items: start;
  background-color: ${theme.colors.grey850};
  border-radius: 8px;
  border: 1px solid ${theme.colors.chalkAlpha20};
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);
  flex-direction: column;
  gap: 8px;
  padding: 14px;
  width: 280px;
`;

const VideoPreviewContainer = styled(Box)`
  aspect-ratio: 16 / 9;
  background-color: ${theme.colors.navy};
  border-radius: 6px;
  overflow: hidden;
  width: 100%;
`;

const VideoPreview = styled.video`
  display: block;
  height: 100%;
  object-fit: cover;
  width: 100%;
`;

const TooltipTitle = styled(Text)`
  line-height: 1.2;
  text-align: left;
`;

const TooltipDescription = styled(Text)`
  line-height: 1.4;
  text-align: left;
  white-space: normal;
  max-width: 200px;
`;

const ManageSignatureLink = styled(Text)`
  align-items: center;
  cursor: pointer;
  display: flex;
  justify-content: center;
  line-height: 1.4;
  padding-top: 4px;
  padding-bottom: 4px;
  text-align: center;
  transition: color 100ms ease;

  &:hover {
    color: ${theme.colors.envy};
  }
`;

const InfoIcon = styled(TbInfoCircle)`
  color: ${theme.colors.error};
`;

const InfoTooltipIcon = styled(TbInfoCircle)`
  color: ${theme.colors.chalkAlpha40};
  cursor: pointer;
  flex-shrink: 0;
  height: 14px;
  width: 14px;
  transition: color 120ms ease;

  &:hover {
    color: ${theme.colors.envy};
  }
`;

const VideoPreviewIcon = styled(TbVideo)`
  color: ${theme.colors.chalkAlpha40};
  cursor: pointer;
  flex-shrink: 0;
  height: 14px;
  width: 14px;
  transition: color 120ms ease;

  &:hover {
    color: ${theme.colors.envy};
  }
`;

const ToggleContainer = styled(Flex)`
  flex-direction: column;
  width: 100%;
`;

const ToggleRow = styled(Flex)<{ clickable?: boolean; $active?: boolean }>`
  align-items: center;
  cursor: ${(props) => (props.clickable ? "pointer" : "default")};
  justify-content: space-between;
  width: 100%;
`;

const Checkbox = styled.div<{ $checked: boolean; $disabled: boolean }>`
  align-items: center;
  background-color: ${({ $checked, theme }) =>
    $checked ? theme.colors.envy : "transparent"};
  border: 2px solid
    ${({ $checked, $disabled, theme }) =>
      $disabled
        ? theme.colors.chalkAlpha10
        : $checked
        ? theme.colors.envy
        : theme.colors.chalkAlpha20};
  border-radius: 4px;
  color: white;
  cursor: ${({ $disabled }) => ($disabled ? "not-allowed" : "pointer")};
  display: flex;
  flex-shrink: 0;
  height: 18px;
  justify-content: center;
  opacity: ${({ $disabled }) => ($disabled ? 0.4 : 1)};
  transition: all 150ms ease;
  width: 18px;

  &:hover {
    border-color: ${({ $disabled, theme }) =>
      $disabled ? theme.colors.chalkAlpha20 : theme.colors.envy};
  }
`;

const ToggleColumn = styled(Flex)`
  align-items: flex-start;
  border-bottom: 1px solid ${(props) => props.theme.colors.chalkAlpha10};
  flex-direction: column;
  justify-content: space-between;
  padding: 10px 16px;
  width: 100%;
  &:last-child {
    border-bottom: none;
  }
`;

const ProToggleColumn = styled(ToggleColumn)<{
  clickable?: boolean;
  onClick?: () => void;
}>`
  background: linear-gradient(
    90deg,
    ${theme.colors.premiumPurpleAlpha05} 0%,
    ${theme.colors.premiumPurpleAlpha03} 50%,
    transparent 100%
  );
  border-left: 2px solid ${theme.colors.premiumPurpleAlpha40};
  cursor: ${(props) => (props.clickable ? "pointer" : "default")};
  position: relative;
`;

const Section = styled(Flex)`
  align-items: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
  width: 100%;
`;

const ThumbnailSection = styled(BaseSection)`
  align-items: center;
  background-color: ${theme.colors.grey850};
  border-bottom: none;
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  justify-content: center;
  padding: 24px;
  width: 25vw;
  @media (max-width: ${(props) => props.theme.breaks.small}) {
    width: 80vw;
  }
`;

const PreviewThumbnail = styled(Image)`
  border-radius: 12px;
  height: 100%;
  width: auto;
`;

const UploadIcon = styled(TbCloudUpload)`
  color: ${theme.colors.chalkAlpha80};
`;

const ThumbnailArea = styled(Flex)<{ showBorder: boolean }>`
  align-items: center;
  aspect-ratio: 16 / 9;
  background-color: ${theme.colors.grey850};
  border-radius: 12px;
  border: 1px dashed ${theme.colors.chalkAlpha40};
  cursor: pointer;
  height: 100%;
  justify-content: center;
  width: 100%;
  &:hover {
    border: 1px dashed ${theme.colors.envy};
    & > svg {
      color: ${theme.colors.envy};
    }
  }
`;

export const SignatureText = styled(Text)`
  transition: color 100ms ease;
  &:hover {
    cursor: pointer;
    color: ${({ theme }) => theme.colors.envy};
  }
`;

export const RemoveText = styled(Text)`
  transition: color 100ms ease;
  &:hover {
    cursor: pointer;
    color: ${({ theme }) => theme.colors.alert};
  }
`;

const WarningText = styled(Text)`
  align-items: center;
  display: flex;
  font-size: 12px;
  gap: 6px;
  line-height: 1.4;
  margin-top: 6px;
  padding: 6px 0;
`;

const ProDivider = styled(Flex)`
  align-items: center;
  background: linear-gradient(
    180deg,
    ${theme.colors.premiumPurpleAlpha10} 0%,
    ${theme.colors.premiumPurpleAlpha05} 50%,
    ${theme.colors.premiumPurpleAlpha03} 100%
  );
  gap: 12px;
  justify-content: center;
  overflow: hidden;
  padding: 20px 24px;
  position: relative;
  width: 100%;
`;

const ProGradientOverlay = styled(Box)`
  background: radial-gradient(
    ellipse at center,
    ${theme.colors.premiumPurpleAlpha20} 0%,
    ${theme.colors.premiumPurpleAlpha10} 40%,
    ${theme.colors.premiumPurpleAlpha05} 70%,
    transparent 100%
  );
  filter: blur(15px);
  height: 60px;
  left: 50%;
  pointer-events: none;
  position: absolute;
  top: 0;
  transform: translateX(-50%);
  width: 100%;
`;

const DividerLine = styled(Box)`
  background: linear-gradient(
    90deg,
    transparent 0%,
    ${theme.colors.chalkAlpha20} 50%,
    transparent 100%
  );
  flex: 1;
  height: 1px;
`;

const DividerText = styled(Text)`
  color: ${theme.colors.premiumPurple};
  font-weight: 700;
  letter-spacing: 1px;
  text-shadow: 0 0 12px ${theme.colors.premiumPurpleAlpha40};
  text-transform: uppercase;
  white-space: nowrap;
`;

const UpgradeText = styled(Text)`
  cursor: pointer;
  text-decoration: underline;
  text-decoration-color: ${theme.colors.premiumPurpleAlpha30};
  transition: all 120ms ease;

  &:hover {
    color: ${theme.colors.ice};
    text-decoration-color: ${theme.colors.premiumPurpleAlpha80};
  }
`;

const UpsellPanel = styled(Flex)`
  flex-direction: column;
  padding: 24px;
  width: 100%;
`;

const UpsellHeader = styled(Flex)<{ $gradient: string }>`
  align-items: center;
  background: ${({ $gradient }) => $gradient};
  border-radius: 8px;
  gap: 8px;
  padding: 12px 16px;
`;

const UpsellFeatureList = styled(Flex)`
  flex-direction: column;
  gap: 8px;
  padding: 16px 0;
`;

const UpsellFeatureItem = styled(Flex)<{ $color: string }>`
  align-items: center;
  gap: 10px;

  svg {
    color: ${({ $color }) => $color};
    flex-shrink: 0;
  }
`;

const HintText = styled(Text)`
  color: ${({ theme }) => theme.colors.chalk};
  font-size: 11px;
  padding: 0 16px;
`;

const CategoryHeader = styled(Text)`
  align-items: center;
  color: ${({ theme }) => theme.colors.envy};
  display: flex;
  font-size: 11px;
  font-weight: 700;
  gap: 6px;
  letter-spacing: 1px;
  padding: 12px 16px 4px;
  text-transform: uppercase;
`;

const FeatureToggleColumn = styled(ToggleColumn)<{
  disabled?: boolean;
  onClick?: () => void;
}>`
  background: ${({ disabled }) =>
    disabled
      ? `linear-gradient(90deg, rgba(78, 189, 148, 0.05) 0%, rgba(78, 189, 148, 0.02) 50%, transparent 100%)`
      : "transparent"};
  border-left: 2px solid
    ${({ disabled, theme }) =>
      disabled ? theme.colors.envyAlpha40 : "transparent"};
  cursor: ${({ disabled }) => (disabled ? "pointer" : "default")};
`;

const TierUpsellDivider = styled(Flex)<{ $tierColor: string }>`
  align-items: center;
  border-bottom: 1px solid ${({ $tierColor }) => `${$tierColor}33`};
  border-top: 1px solid ${({ $tierColor }) => `${$tierColor}33`};
  justify-content: center;
  padding: 14px 24px;
  width: 100%;
`;

const TierUpsellButton = styled.button<{ $gradient: string }>`
  background: ${({ $gradient }) => $gradient};
  border: none;
  border-radius: 6px;
  color: white;
  cursor: pointer;
  font-size: 13px;
  font-weight: 600;
  padding: 8px 24px;
  transition: opacity 150ms ease;
  white-space: nowrap;

  &:hover {
    opacity: 0.9;
  }
`;

interface IPremiumFeatureWrapperProps {
  isDisabled: boolean;
  featureName: string;
  onPremiumClick: (name: string) => void;
  children: React.ReactNode;
}

export const PremiumFeatureWrapper: React.FC<IPremiumFeatureWrapperProps> = ({
  isDisabled,
  featureName,
  onPremiumClick,
  children,
}) => {
  return (
    <ProToggleColumn
      clickable={isDisabled}
      onClick={() => isDisabled && onPremiumClick(featureName)}
    >
      {children}
    </ProToggleColumn>
  );
};
